// A routine's end, as every surface shows it: "Runs until <date>" while it is live, "Ended <date>" once it is
// over, and then no next run and no switch (resuming it without a later end would only end it again; the user
// keeps it going by asking the assistant for a new end). `endsAt` and `ended` come from the server's
// `routines` action (handlers/routines.ts `computeRoutineEnd`). Mirrored by iOS `RoutineEnd.swift` and
// Android `RoutineEnd` in RoutineStatus.kt.

export interface EndableRoutine {
  /** When the routine's job is over (ISO), absent/null for an open-ended routine. */
  endsAt?: string | null;
  /** The routine has ended (its end passed, or the pulse service ended it). */
  ended?: boolean;
}

/** The end as a medium date in the reader's locale and zone ("Oct 9, 2026"), or null when it has none. */
export function routineEndDate(routine: EndableRoutine, locale?: string, timeZone?: string): string | null {
  if (!routine.endsAt) return null;
  const at = new Date(routine.endsAt);
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', ...(timeZone ? { timeZone } : {}) }).format(at);
}

/** The date for "Ended {date}", or null: only an ended routine with a known end says it. */
export function endedOnDate(routine: EndableRoutine, locale?: string, timeZone?: string): string | null {
  return routine.ended === true ? routineEndDate(routine, locale, timeZone) : null;
}

/** The date for the "Runs until" row, or null: only a live routine with an end has one. */
export function runsUntilDate(routine: EndableRoutine, locale?: string, timeZone?: string): string | null {
  return routine.ended === true ? null : routineEndDate(routine, locale, timeZone);
}

/** An ended routine has no next run and no switch. */
export function routineShowsNextRunAndToggle(routine: EndableRoutine): boolean {
  return routine.ended !== true;
}
